import { useState, type ReactNode } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { formatWithNumeral } from '@/shared/utils/numbers';

type DualRangeSliderProps = {
  min?: number;
  max?: number;
  sliderValues: [number, number];
  setSliderValues: (values: [number, number]) => void;
  format?: (value: number) => ReactNode;
  step?: number;
  ariaLabel?: string;
};

export const DualRangeSlider = ({
  min = 0,
  max = 1000000,
  sliderValues,
  setSliderValues,
  format = formatWithNumeral,
  step = 1,
  ariaLabel,
}: DualRangeSliderProps) => {
  const [activeThumb, setActiveThumb] = useState<number | null>(null);

  return (
    <div className="flex w-full gap-x-2">
      <div className="font-aeonikMedium text-12 text-cf-light-2">{format(min)}</div>
      <SliderPrimitive.Root
        min={min}
        max={max}
        step={step}
        aria-label={ariaLabel}
        value={sliderValues}
        onValueChange={setSliderValues}
        className="relative flex w-full touch-none select-none items-center"
      >
        <SliderPrimitive.Track className="relative h-1 flex-grow rounded-full bg-cf-gray-5">
          <SliderPrimitive.Range className="absolute h-full rounded-full bg-cf-green-1" />
        </SliderPrimitive.Track>
        {sliderValues.map((value, index) => (
          <SliderPrimitive.Thumb
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="group relative z-10 block h-[11px] w-[11px] rounded-full border-2 border-cf-green-1 bg-cf-gray-5 focus:outline-none"
            onPointerDown={() => setActiveThumb(index)}
            onPointerUp={() => setActiveThumb(null)}
          >
            {activeThumb === index && (
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border-cf-light-1 bg-cf-gray-5 px-1.5 py-0.5 font-aeonikMedium text-10 leading-[20px] text-cf-light-3 shadow">
                {format(value)}
              </div>
            )}
          </SliderPrimitive.Thumb>
        ))}
      </SliderPrimitive.Root>
      <div className="font-aeonikMedium text-12 text-cf-light-2">{format(max)}</div>
    </div>
  );
};
